import { useCallback } from 'react';
import { PlaceholderContainerCompat } from './PlaceholderContainerCompat';
import { usePlaceholderState } from './usePlaceholderState';
import { adjustIndexForRemovedItem } from './adjustIndexForRemovedItem';
import { useHasReorderingGate } from '../../hasReorderingGate';

/**
 * Hook that interleaves placeholder elements into a list during drag operations.
 *
 * Removes the dragged item from its original position and inserts a placeholder
 * at the target position, adjusting indices accordingly.
 *
 * Example: [A, B, C, D] dragging B after C → [A, C, <Placeholder>, D]
 *
 * @returns Memoized function that adds drag placeholders to element arrays
 */
import { jsx as _jsx } from "react/jsx-runtime";
export function useInterleavePlaceholder() {
  const {
    fromIndex,
    isDragging,
    size,
    toIndex
  } = usePlaceholderState();
  const hasReorderingGate = useHasReorderingGate();
  return useCallback(elements => {
    // Early return - no drag operation in progress
    if (!hasReorderingGate || !isDragging || toIndex == null || fromIndex == null || size == null) {
      return elements;
    }
    const elementsWithoutDraggingItem = elements.filter((_, index) => index !== fromIndex);
    const placeholder = /*#__PURE__*/_jsx(PlaceholderContainerCompat, {
      as: "th",
      size: size,
      targetIndex: toIndex
    }, `placeholder-${toIndex}`);
    const targetIndex = adjustIndexForRemovedItem(toIndex, fromIndex);
    const result = [...elementsWithoutDraggingItem];
    result.splice(targetIndex, 0, placeholder);
    return result;
  }, [fromIndex, hasReorderingGate, isDragging, size, toIndex]);
}